/**
 * Core Calendar Component
 *
 * Styled wrapper around react-day-picker (DayPicker v9).
 * Follows shadcn/ui calendar pattern — composable with Popover to build DatePicker.
 *
 * @example
 *   <Calendar mode="single" selected={date} onSelect={setDate} />
 *   <Calendar mode="range" selected={range} onSelect={setRange} />
 */
import React from 'react';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Import the official base CSS — provides correct layout/positioning
import 'react-day-picker/src/style.css';

export type CalendarProps = DayPickerProps & {
  className?: string;
};

/**
 * Theme overrides applied on top of the official rdp v9 base CSS.
 * Only overrides colors, typography, spacing — NOT layout/positioning.
 */
const themeOverrides = `
/* ── Variables ── */
.rdp-root {
  --rdp-accent-color: var(--primary-color, #3b82f6);
  --rdp-accent-background-color: color-mix(in srgb, var(--primary-color, #3b82f6) 12%, transparent);
  --rdp-day-height: 36px;
  --rdp-day-width: 36px;
  --rdp-day_button-border-radius: 6px;
  --rdp-day_button-border: 2px solid transparent;
  --rdp-day_button-height: 34px;
  --rdp-day_button-width: 34px;
  --rdp-selected-border: 2px solid transparent;
  --rdp-today-color: var(--primary-color, #3b82f6);
  --rdp-outside-opacity: 0.4;
  --rdp-disabled-opacity: 0.3;
  --rdp-nav_button-height: 28px;
  --rdp-nav_button-width: 28px;
  --rdp-nav-height: 36px;
  --rdp-months-gap: 1rem;
  --rdp-range_start-color: #fff;
  --rdp-range_start-date-background-color: var(--primary-color, #3b82f6);
  --rdp-range_end-color: #fff;
  --rdp-range_end-date-background-color: var(--primary-color, #3b82f6);
  --rdp-range_middle-background-color: color-mix(in srgb, var(--primary-color, #3b82f6) 10%, transparent);
  --rdp-range_middle-color: inherit;
}

/* ── Caption ── */
.rdp-root .rdp-month_caption {
  font-size: 0.875rem;
  font-weight: 500;
  height: var(--rdp-nav-height);
}
.rdp-root .rdp-caption_label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
}

/* ── Nav buttons ── */
.rdp-root .rdp-button_previous,
.rdp-root .rdp-button_next {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: var(--rdp-nav_button-width);
  height: var(--rdp-nav_button-height);
  transition: background-color 150ms, border-color 150ms;
}
.rdp-root .rdp-button_previous:hover,
.rdp-root .rdp-button_next:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

/* ── Weekdays ── */
.rdp-root .rdp-weekday {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  opacity: 1;
}

/* ── Day cells ── */
.rdp-root .rdp-day_button {
  font-size: 0.875rem;
  border-radius: var(--rdp-day_button-border-radius);
  transition: background-color 100ms;
}
.rdp-root .rdp-day_button:hover {
  background: #f1f5f9;
}
.rdp-root .rdp-day_button:focus-visible {
  outline: 2px solid var(--primary-color, #3b82f6);
  outline-offset: 1px;
}

/* ── Selected — override the ugly "bold + font-size: large" default ── */
.rdp-root .rdp-selected {
  font-weight: 500;
  font-size: inherit;
}
.rdp-root .rdp-selected .rdp-day_button {
  background-color: var(--primary-color, #3b82f6);
  color: #fff;
  border-color: transparent;
}
.rdp-root .rdp-selected .rdp-day_button:hover {
  background-color: var(--primary-color, #3b82f6);
  opacity: 0.9;
}

/* ── Today ── */
.rdp-root .rdp-today:not(.rdp-outside) {
  color: inherit;
}
.rdp-root .rdp-today:not(.rdp-selected) .rdp-day_button {
  background: #f1f5f9;
  font-weight: 600;
}

/* ── Range middle — clean flat highlight ── */
.rdp-root .rdp-range_middle .rdp-day_button {
  color: inherit;
}

/* ── Dropdown selects (DOB mode) ── */
.rdp-root .rdp-dropdown_root {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 0.25rem;
  font-size: 0.8125rem;
  font-weight: 500;
}
.rdp-root .rdp-dropdown_root .rdp-caption_label {
  padding: 0.25rem 0.35rem;
}
.rdp-root .rdp-dropdown_root .rdp-chevron {
  display: inline-flex;
  margin-left: 0.125rem;
}

/* ── DARK MODE ── */
.dark .rdp-root .rdp-caption_label { color: #f1f5f9; }
.dark .rdp-root .rdp-weekday { color: #94a3b8; }
.dark .rdp-root .rdp-button_previous,
.dark .rdp-root .rdp-button_next {
  border-color: #334155;
  color: #94a3b8;
}
.dark .rdp-root .rdp-button_previous:hover,
.dark .rdp-root .rdp-button_next:hover {
  background: #1e293b;
  border-color: #475569;
}
.dark .rdp-root .rdp-day_button:hover { background: #1e293b; }
.dark .rdp-root .rdp-today:not(.rdp-selected) .rdp-day_button { background: #1e293b; }
.dark .rdp-root .rdp-dropdown_root {
  border-color: #334155;
  color: #e2e8f0;
}
`;

// Inject theme overrides once
let injected = false;
function injectTheme() {
  if (injected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.setAttribute('data-calendar-theme', '');
  el.textContent = themeOverrides;
  document.head.appendChild(el);
  injected = true;
}

/**
 * Fully themed calendar grid — used standalone or inside DatePicker.
 */
export const Calendar: React.FC<CalendarProps> = ({
  className,
  showOutsideDays = true,
  ...props
}) => {
  React.useEffect(() => { injectTheme(); }, []);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
};

Calendar.displayName = 'Calendar';
export default Calendar;
